import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCreditCard, FiBookOpen, FiShoppingBag, FiCalendar, FiCheckCircle, FiSearch } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';

const PaymentHistoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-light)' : 'var(--background-secondary)'};
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HistoryCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-primary) 100%);
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const TypeIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 1.25rem;
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
`;

const TypeLabel = styled.h3`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CardDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  line-height: 1.4;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ItemName = styled.h4`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ItemDescription = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  margin: 0 0 0.5rem 0;
  line-height: 1.5;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const DescriptionText = styled.span`
  flex: 1;
`;

const PaymentDetails = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (max-width: 480px) {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    gap: 0.375rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const DiscountBadge = styled.span`
  color: var(--success-color);
  font-weight: 600;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  
  ${props => props.status === 'completed' && `
    background: rgba(34, 197, 94, 0.1);
    color: var(--success-color);
    border: 1px solid rgba(34, 197, 94, 0.2);
  `}
  
  ${props => props.status === 'pending' && `
    background: rgba(245, 158, 11, 0.1);
    color: var(--warning-color);
    border: 1px solid rgba(245, 158, 11, 0.2);
  `}
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
  }
`;

const AmountSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--success-color);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.125rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
    align-self: flex-end;
  }
`;

const ReceiptLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  margin-top: 0.5rem;

  &:hover {
    color: var(--primary-dark);
    text-decoration: underline;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--background-secondary);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: var(--text-secondary);
  font-size: 2rem;
  
  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const ErrorMessage = styled.div`
  background: var(--error-color);
  border: 1px solid var(--error-color);
  border-radius: 0;
  padding: 1rem;
  color: var(--text-inverse);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8rem;
  }
`;

const InfoMessage = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 0;
  padding: 1rem;
  color: var(--info-color);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8rem;
  }
`;

function PaymentHistoryPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    if (user?.id) {
      fetchPaymentHistory();
    }
  }, [user?.id]);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch both course enrollments and product purchases
      const [enrollmentsResponse, purchasesResponse] = await Promise.all([
        api.getUserEnrollments(),
        api.getUserPurchases()
      ]);

      // Filter date threshold - only show purchases after 09/16/2025
      const historyStartDate = new Date('2025-09-16');
      
      // Combine and filter by date
      const allHistory = [
        ...enrollmentsResponse.enrollments
          .filter(enrollment => {
            if (enrollment.payment_status === 'pending') {
              return false;
            }

            // Show all enrollments with successful payment transactions (paid courses)
            // For free courses, only show if after the start date
            if (enrollment.payment_status === 'succeeded' && enrollment.amount > 0) {
              return true; // Always show successful paid enrollments
            }
            // For free courses or enrollments without payment info, apply date filter
            const enrollmentDate = new Date(enrollment.enrolled_at);
            return enrollmentDate >= historyStartDate;
          })
          .map(enrollment => ({
            id: `enrollment-${enrollment.id}`,
            type: 'course',
            typeIcon: <FiBookOpen />,
            typeLabel: 'Course Enrollment',
            name: enrollment.course_name,
            description: `${enrollment.course_code} - ${enrollment.academic_year || '2025-2026'}`,
            amount: parseFloat(enrollment.amount) || 0,
            originalPrice: parseFloat(enrollment.original_price) || 0,
            date: enrollment.enrolled_at,
            purchaseDate: enrollment.enrolled_at,
            orderNumber: `CE-${String(enrollment.id).padStart(6, '0')}`,
            status: enrollment.payment_status === 'succeeded' ? 'completed' : 'completed',
            receiptUrl: enrollment.receipt_url,
            paymentIntentId: enrollment.payment_intent_id || null
          })),
        ...purchasesResponse.purchases
          .filter(purchase => purchase.payment_status !== 'pending' && new Date(purchase.purchase_date) >= historyStartDate)
          .map(purchase => ({
            id: `purchase-${purchase.id}`,
            type: 'product',
            typeIcon: <FiShoppingBag />,
            typeLabel: 'Product Purchase',
            name: purchase.product_name,
            description: purchase.product_type,
            amount: purchase.total_amount,
            date: purchase.purchase_date,
            purchaseDate: purchase.purchase_date,
            orderNumber: `PP-${String(purchase.id).padStart(6, '0')}`,
            status: purchase.payment_status === 'succeeded' ? 'completed' : purchase.payment_status,
            receiptUrl: purchase.receipt_url
          }))
      ];

      // Sort by date (newest first)
      allHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setHistory(allHistory);
    } catch (error) {
      logger.error('Error fetching payment history', { error });
      // If it's a 404, 500, or route not found, show the date restriction message instead of error
      if (error.message.includes('Route not found') || 
          error.message.includes('404') || 
          error.message.includes('500') ||
          error.message.includes('Failed to fetch')) {
        setError('Payment history is only available for purchases made after 09/16/2025');
      } else {
        setError('Failed to load payment history. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateForDisplay(dateString, 'MMMM d, yyyy \'at\' h:mm a');
  };

  const formatPurchaseDate = (dateString) => {
    return formatDateForDisplay(dateString, 'MMMM d, yyyy');
  };

  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const filteredHistory = history.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      activeFilter === 'all' || 
      (activeFilter === 'courses' && item.type === 'course') ||
      (activeFilter === 'products' && item.type === 'product');
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <PaymentHistoryContainer>
        <Header>
          <Title>Payment History</Title>
        </Header>
        <LoadingState>
          Loading payment history...
        </LoadingState>
      </PaymentHistoryContainer>
    );
  }

  return (
    <PaymentHistoryContainer>
      <Header>
        <Title>Payment History</Title>
        <SearchBar>
          <SearchIcon>
            <FiSearch />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search payment history..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>
      </Header>

      <Filters>
        <FilterButton 
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
        >
          All Transactions
        </FilterButton>
        <FilterButton 
          active={activeFilter === 'courses'}
          onClick={() => setActiveFilter('courses')}
        >
          Course Enrollments
        </FilterButton>
        <FilterButton 
          active={activeFilter === 'products'}
          onClick={() => setActiveFilter('products')}
        >
          Product Purchases
        </FilterButton>
      </Filters>

      {error && (
        error.includes('only available') ? (
          <InfoMessage>
            {error}
          </InfoMessage>
        ) : (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )
      )}

      {!error && filteredHistory.length === 0 && (
        <EmptyState>
          <EmptyIcon>
            <FiCreditCard />
          </EmptyIcon>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontFamily: "'Inter', sans-serif", fontSize: '1.25rem', fontWeight: '600' }}>
            {searchTerm || activeFilter !== 'all' ? 'No matching transactions found' : 'No Payment History'}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            {searchTerm || activeFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Payment history is only available for purchases made after 09/16/2025.'
            }
          </p>
        </EmptyState>
      )}

      {!error && filteredHistory.length > 0 && (
        <HistoryList>
          {filteredHistory.map((item, index) => (
            <HistoryCard
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <CardHeader>
                <CardTitle>
                  <TypeIcon>
                    {item.typeIcon}
                  </TypeIcon>
                  <TypeLabel>{item.typeLabel}</TypeLabel>
                </CardTitle>
                <HeaderMeta>
                  <CardDate>
                    <FiCalendar />
                    {formatDate(item.date)}
                  </CardDate>
                  <MetaRow>Purchase Date: {formatPurchaseDate(item.purchaseDate)}</MetaRow>
                  <MetaRow>Order Number: {item.orderNumber}</MetaRow>
                </HeaderMeta>
              </CardHeader>
              
              <CardContent>
                <ItemName>{item.name}</ItemName>
                <ItemDescription>
                  <DescriptionText>{item.description}</DescriptionText>
                  <StatusBadge status={item.status}>
                    <FiCheckCircle />
                    {item.status === 'completed' ? 'Completed' : 'Pending'}
                  </StatusBadge>
                </ItemDescription>
                
                {/* Additional payment details for courses */}
                {item.type === 'course' && item.originalPrice > 0 && (
                  <PaymentDetails>
                    {item.originalPrice !== item.amount && (
                      <DetailItem>
                        <DetailLabel>Original Price:</DetailLabel>
                        <DetailValue>{formatAmount(item.originalPrice)}</DetailValue>
                      </DetailItem>
                    )}
                    {item.originalPrice !== item.amount && (
                      <DetailItem>
                        <DetailLabel>Discount:</DetailLabel>
                        <DiscountBadge>-{formatAmount(item.originalPrice - item.amount)}</DiscountBadge>
                      </DetailItem>
                    )}
                    {item.paymentIntentId && (
                      <DetailItem>
                        <DetailLabel>Payment ID:</DetailLabel>
                        <DetailValue style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {item.paymentIntentId}
                        </DetailValue>
                      </DetailItem>
                    )}
                  </PaymentDetails>
                )}
                
                <CardFooter>
                  <div>
                    {item.receiptUrl && (
                      <ReceiptLink href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                        View Receipt
                      </ReceiptLink>
                    )}
                  </div>
                  
                  <AmountSection>
                    {formatAmount(item.amount)}
                  </AmountSection>
                </CardFooter>
              </CardContent>
            </HistoryCard>
          ))}
        </HistoryList>
      )}
    </PaymentHistoryContainer>
  );
}

export default PaymentHistoryPage;
